import { useEffect, useCallback, useRef, useMemo } from 'react'
import { supabase } from '../../../lib/supabase'
import { useChatStore } from '../../../stores/chatStore'
import * as Sentry from '@sentry/react'

interface Conversation {
    id: string
    title: string
    agent_id: string | null
    created_at: string
    updated_at: string
}

const EMPTY_ARRAY: any[] = []

export const useChatConversations = (
    userId: string | null,
    agentId: string | null
) => {
    // Actions (Stable selectors)
    const setConversations = useChatStore(state => state.setConversations)
    const setCurrentConversation = useChatStore(state => state.setCurrentConversation)
    const setLoadingConversations = useChatStore(state => state.setLoadingConversations)

    const conversations = useChatStore(useCallback(state =>
        agentId ? state.agents[agentId]?.conversations || EMPTY_ARRAY : EMPTY_ARRAY
        , [agentId]))

    const currentConversationId = useChatStore(useCallback(state =>
        agentId ? state.agents[agentId]?.currentConversationId || null : null
        , [agentId]))

    // Check LOADED flag
    const isLoaded = useChatStore(useCallback(state =>
        agentId ? state.agents[agentId]?.conversationsLoaded || false : false
        , [agentId]))

    const isLoading = useChatStore(useCallback(state =>
        agentId ? state.agents[agentId]?.isLoadingConversations || false : false
        , [agentId]))

    const loadingRef = useRef(false)
    const firstLoadRef = useRef(true)

    // Load conversations
    const loadConversations = useCallback(async () => {
        if (!userId || !agentId) return
        if (loadingRef.current) return

        loadingRef.current = true
        setLoadingConversations(agentId, true)

        try {
            let query = supabase
                .from('conversations')
                .select('*')
                .eq('user_id', userId)
                .order('updated_at', { ascending: false })

            if (agentId) {
                query = query.eq('agent_id', agentId)
            } else if (agentId === null) {
                query = query.is('agent_id', null)
            }

            const { data, error } = await query

            if (error) throw error

            const newConversations = (data || []) as unknown as Conversation[]
            setConversations(agentId, newConversations)

            // Phase 3.2: Smart Auto-Selection
            // We only auto-select the latest conversation on FIRST load of the agent context.
            // If the user later clears it (New Chat), we respect that 'null' state.
            const state = useChatStore.getState()
            const currentId = state.agents[agentId]?.currentConversationId

            if (firstLoadRef.current) {
                const isValid = currentId && newConversations.find(c => c.id === currentId)
                if (!isValid) {
                    const latest = newConversations.length > 0 ? newConversations[0].id : null
                    setCurrentConversation(agentId, latest)
                }
                firstLoadRef.current = false
            }

        } catch (err) {
            Sentry.captureException(err, {
                tags: { hook: 'useChatConversations', action: 'loadConversations' },
                extra: { userId, agentId }
            })
        } finally {
            setLoadingConversations(agentId, false)
            loadingRef.current = false
        }
    }, [userId, agentId, setConversations, setLoadingConversations, setCurrentConversation])

    // Effect
    useEffect(() => {
        if (agentId) {
            if (!isLoaded && !isLoading) {
                loadConversations()
            }
        }
    }, [agentId, isLoaded, isLoading, loadConversations])

    // Create a new conversation
    const createConversation = useCallback(async (title?: string) => {
        if (!userId || !agentId) return null

        try {
            const newConversation = {
                user_id: userId,
                agent_id: agentId,
                title: title || 'New Conversation',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            }

            const { data, error } = await supabase
                .from('conversations')
                .insert(newConversation)
                .select()
                .single()

            if (error) throw error

            const conversation = data as unknown as Conversation

            const state = useChatStore.getState()
            const currentConvs = state.agents[agentId]?.conversations || []

            setConversations(agentId, [conversation, ...currentConvs])
            setCurrentConversation(agentId, conversation.id)

            return conversation
        } catch (err) {
            Sentry.captureException(err, {
                tags: { hook: 'useChatConversations', action: 'createConversation' },
                extra: { userId, agentId }
            })
            return null
        }
    }, [userId, agentId, setConversations, setCurrentConversation])

    // Switch to a different conversation
    const switchConversation = useCallback((conversationId: string) => {
        if (agentId) {
            setCurrentConversation(agentId, conversationId)
        }
    }, [agentId, setCurrentConversation])

    // Delete a conversation
    const deleteConversation = useCallback(async (conversationId: string) => {
        if (!agentId) return
        try {
            const { error } = await supabase
                .from('conversations')
                .delete()
                .eq('id', conversationId)

            if (error) throw error

            const state = useChatStore.getState()
            const currentConvs = state.agents[agentId]?.conversations || []
            const newConvs = currentConvs.filter(c => c.id !== conversationId)

            setConversations(agentId, newConvs)

            const currentId = state.agents[agentId]?.currentConversationId
            if (currentId === conversationId) {
                const latest = newConvs.length > 0 ? newConvs[0].id : null
                setCurrentConversation(agentId, latest)
            }
        } catch (err) {
            Sentry.captureException(err, {
                tags: { hook: 'useChatConversations', action: 'deleteConversation' },
                extra: { conversationId }
            })
        }
    }, [agentId, setConversations, setCurrentConversation])

    // Reset to "New Conversation" state (no ID)
    const resetConversation = useCallback(() => {
        if (agentId) {
            setCurrentConversation(agentId, null)
            // Also clear messages to show blank slate
            const clearMessages = useChatStore.getState().clearMessages
            clearMessages(agentId)
        }
    }, [agentId, setCurrentConversation])

    return useMemo(() => ({
        conversations,
        currentConversationId,
        isLoading,
        isLoaded,
        loadConversations,
        createConversation,
        switchConversation,
        deleteConversation,
        resetConversation
    }), [
        conversations,
        currentConversationId,
        isLoading,
        isLoaded,
        loadConversations,
        createConversation,
        switchConversation,
        deleteConversation,
        resetConversation
    ])
}
